import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { isUserPreviewActive, setPreviewMode } from '../utils/adminPreview';
import { SectionKey, SECTION_ROUTE_SLUGS, SLUG_TO_SECTION } from '../components/layout';
import * as dashboardApi from '../../../api/dashboardApi';

const DEFAULT_SECTION: SectionKey = 'overview';

export const useAdminNavigation = () => {
  const navigate = useNavigate();
  const { section: sectionSlug } = useParams<{ section?: string }>();
  
  // Open dashboard with sidebar collapsed by default for a cleaner initial view
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [activeSection, setActiveSection] = useState<SectionKey>(DEFAULT_SECTION);
  const [selectedRegulation, setSelectedRegulation] = useState<dashboardApi.RegulationSummary | null>(null);
  const [userPreviewActive, setUserPreviewActive] = useState(false);

  useEffect(() => {
    setUserPreviewActive(isUserPreviewActive());
  }, []);

  const location = useLocation();

  useEffect(() => {
    // Only attempt to normalize/redirect when the current pathname is under /admin/dashboard
    if (!location.pathname.startsWith('/admin/dashboard')) return;

    // Prefer the `:section` param when available, otherwise extract the first segment
    // following `/admin/dashboard/` from the pathname so explicit routes such as
    // `/admin/dashboard/generated-transcripts` are honored.
    let normalizedSlug = sectionSlug?.toLowerCase();
    if (!normalizedSlug) {
      const m = location.pathname.match(/^\/admin\/dashboard\/([^/]+)/i);
      if (m && m[1]) normalizedSlug = m[1].toLowerCase();
    }

    // If no slug present, redirect to default section (e.g. when visiting `/admin/dashboard`).
    if (!normalizedSlug) {
      navigate(`/admin/dashboard/${SECTION_ROUTE_SLUGS[DEFAULT_SECTION]}`, { replace: true });
      return;
    }

    if (normalizedSlug === 'regulations-breakdown') {
      if (activeSection !== 'charts') {
        setActiveSection('charts');
      }
      return;
    }

    const mappedSection = SLUG_TO_SECTION[normalizedSlug];
    // If the slug isn't one of the dashboard sections (for example a special
    // page like `generated-transcripts`), do not override it by redirecting
    // back to the default dashboard section — simply skip mapping.
    if (!mappedSection) {
      return;
    }

    if (mappedSection !== activeSection) {
      setActiveSection(mappedSection);
    }
  }, [sectionSlug, navigate, activeSection, location.pathname]);

  const handleNavClick = useCallback((section: SectionKey) => {
    // Notify pages to reset filters when navigating between admin sections
    if (typeof window !== 'undefined') {
      try {
        window.dispatchEvent(new CustomEvent('admin:reset-filters'));
      } catch (e) {
        // ignore
      }
    }
    setActiveSection(section);
    const slug = SECTION_ROUTE_SLUGS[section] ?? SECTION_ROUTE_SLUGS[DEFAULT_SECTION];
    navigate(`/admin/dashboard/${slug}`);
  }, [navigate]);

  const handleLogout = useCallback(() => {
    const token = window.localStorage.getItem('policyora_token');
    const baseUrl = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';
    if (token) {
      fetch(`${baseUrl}/api/v1/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
    window.localStorage.removeItem('policyora_token');
    window.localStorage.removeItem('auth_meta');
    navigate('/admin/login');
  }, [navigate]);

  const switchToAdminView = useCallback(() => {
    setPreviewMode('admin');
    setUserPreviewActive(false);
    if (!window.location.pathname.startsWith('/admin')) {
      navigate('/admin/dashboard');
    }
  }, [navigate]);

  const switchToUserView = useCallback(() => {
    setPreviewMode('user');
    setUserPreviewActive(true);
    navigate('/dashboard?adminPreview=1');
  }, [navigate]);

  const handleRegulationInspect = useCallback((regulation: dashboardApi.RegulationSummary) => {
    setSelectedRegulation(regulation);
  }, []);

  const handleTimelineItemClick = useCallback((item: dashboardApi.TimelineItem) => {
    const summary: dashboardApi.RegulationSummary = {
      id: item.id,
      title: item.title,
      title_short: item.title_short,
      jurisdiction_name: item.jurisdiction_name,
      jurisdiction_code: item.jurisdiction_code,
      regulation_type: item.regulation_type,
      status: item.status || '',
      last_action_date: item.last_action_date || (item.date_type === 'last_action_date' ? item.date : undefined),
      effective_date: item.effective_date || (item.date_type === 'effective_date' ? item.date : undefined),
      source_url: item.source_url,
      summary: item.summary,
    };
    setSelectedRegulation(summary);
  }, []);

  return {
    sidebarCollapsed,
    setSidebarCollapsed,
    activeSection,
    selectedRegulation,
    setSelectedRegulation,
    userPreviewActive,
    handleNavClick,
    handleLogout,
    switchToAdminView,
    switchToUserView,
    handleRegulationInspect,
    handleTimelineItemClick,
  };
};
